import defs from "./stepDefinitions.json";

type StepDomain = "processing" | "syndication" | "storage";
type StepRenderer = "dynamic" | "component";
type FieldType = "string" | "number" | "boolean" | "select";
type RuleKind =
  | "vumoApiEndpointFromProfile"
  | "vumoApiEndpointFromProfileManual"
  | "vumoApiEndpointFromProfilePano"
  | "brightRiverCallback"
  | "constant"
  | "constantIfMissing";

export interface StepFieldOption {
  label: string;
  value: string;
}

export interface StepFieldDefinition {
  key: string;
  label: string;
  type: FieldType;
  inputType?: string;
  disabled?: boolean;
  hidden?: boolean;
  inputMaxLength?: number;
  options?: StepFieldOption[];
  visibleWhen?: {
    field: string;
    equals: string | number | boolean;
  };
}

export interface StepFlagDefinition {
  key: string;
  excludeIfEnabled?: string[];
}

export interface StepDerivedRule {
  target: string;
  ruleName?: RuleKind;
  kind?: RuleKind;
  dependsOn: string[];
  value?: any;
}

export interface StepDefinition {
  id: string;
  service_description: string;
  service_flag: string[];
  service_category: StepDomain;
  label: string;
  order: number;
  renderer: StepRenderer;
  stepFlags: StepFlagDefinition[];
  defaults?: Record<string, any>;
  fields?: StepFieldDefinition[];
  autoPopulateRules?: StepDerivedRule[];
  derivedRules?: StepDerivedRule[];
}

interface StepDefsPayload {
  services: Record<string, any>;
}

const payload = defs as StepDefsPayload;
const VUMO_API_BASE_URL = "https://api.vumography.vumo.ai/api/v1/process/single";
const VUMO_DUAL_PROFILE = "platform_30_off_roll_compensation_car_crop_09";

const resolveProfile = (rawProfile: unknown, fallback = ""): string => {
  return String(rawProfile || fallback).trim();
};

const buildVumoEndpoint = (profile: string, allowDualProfile = true): string => {
  if (!profile) return "";
  if (allowDualProfile && profile === VUMO_DUAL_PROFILE) {
    return `${VUMO_API_BASE_URL}/${VUMO_DUAL_PROFILE}, ${VUMO_API_BASE_URL}/platform_30_static_image`;
  }
  return `${VUMO_API_BASE_URL}/${profile}`;
};

export const getStepDefinitions = (domain: StepDomain): StepDefinition[] => {
  return Object.entries(payload.services)
    .filter(([, def]) => (def.service_category || def.category) === domain)
    .map(([id, def]) => ({
      id,
      service_description: def.service_description || def.label || id,
      service_flag: Array.isArray(def.service_flag)
        ? def.service_flag
        : Array.isArray(def.stepFlags)
          ? def.stepFlags.map((f: any) => f.key).filter(Boolean)
          : [],
      service_category: def.service_category || def.category,
      label: def.label || def.service_description || id,
      order: def.order,
      renderer: def.renderer,
      stepFlags: def.stepFlags,
      defaults: def.defaults,
      fields: def.fields,
      autoPopulateRules: def.autoPopulateRules || def.derivedRules,
      derivedRules: def.derivedRules,
    }))
    .sort((a, b) => a.order - b.order);
};

export const getStepDefinitionMap = (domain: StepDomain): Record<string, StepDefinition> => {
  return getStepDefinitions(domain).reduce<Record<string, StepDefinition>>((acc, item) => {
    acc[item.id] = item;
    return acc;
  }, {});
};

export const applyDerivedRule = (
  rule: StepDerivedRule,
  config: Record<string, any>
): any => {
  const ruleName = rule.ruleName;
  switch (ruleName) {
    case "vumoApiEndpointFromProfile": {
      if (config.processor !== "vumo") return config.apiEndpoint || "";
      return buildVumoEndpoint(resolveProfile(config.profile));
    }
    case "vumoApiEndpointFromProfileManual": {
      return buildVumoEndpoint(resolveProfile(config.profile, "default_detail"));
    }
    case "vumoApiEndpointFromProfilePano": {
      return buildVumoEndpoint(resolveProfile(config.profile, "default_interior_simple"), false);
    }
    case "brightRiverCallback":
      return config.processor === "br"
        ? "www.bwiportal.com/br/completed"
        : config.callBackURL || "";
    case "constant":
      return rule.value as string;
    case "constantIfMissing":
      return (config[rule.target] ?? rule.value) as string;
    default:
      return "";
  }
};
